import {DashboardFeedbackDialog, type FeedbackDialogRef} from '@github-ui/dashboard-feedback-dialog'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Filter, type FilterProps} from '@github-ui/filter'
import {SubmitEvent} from '@github-ui/filter-core'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {SidebarTooltip} from '@github-ui/layout-primitives/SidebarTooltip'
import {NewRepoAgentTaskButton} from '@github-ui/new-repo-agent-task-button/NewRepoAgentTaskButton'
import {noop} from '@github-ui/noop'
import {repoPullRequestsPath} from '@github-ui/paths'
import {useReportErrorContext} from '@github-ui/react-core/future/report-error-context'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useFilterProviders} from '@github-ui/shared-filter-providers/providers/use-filter-providers'
import {DashboardSidebarCollapsible} from '@github-ui/shared-dashboard-sidebar/DashboardSidebarCollapsible'
import {SharedHeader} from '@github-ui/shared-dashboard-surface/components/shared/SharedHeader'
import {isStaff, sendCustomMetric} from '@github-ui/stats'
import {useAnalytics} from '@github-ui/use-analytics'
import {reactFetch} from '@github-ui/verified-fetch'
import {
  AgentIcon,
  ArrowSwitchIcon,
  ArrowUpRightIcon,
  CommentIcon,
  MilestoneIcon,
  RocketIcon,
  TagIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, CounterLabel, Label} from '@primer/react'
import {useId, useRef, useState} from 'react'

import {useRepoPullsSearch, useRepoPullsSidebar} from '../hooks/use-repo-pulls-dashboard'
import {repoPullsDashboardRepositoryToRepository} from '../helpers/repo-pulls-dashboard-proto'
import {repoPullsDashboardContentRoute} from './repo-pulls-dashboard-content-route'
import {repoPullsDashboardLayoutRoute} from './repo-pulls-dashboard-layout-route'
import {navigateToRepoPulls} from './navigate-to-repo-pulls'

import styles from './RepoPullsDashboardHeader.module.css'

const EMPTY_SAVED_VIEWS: never[] = []
const STAFF_FEEDBACK_URL = 'https://gh.io/pr-dashboard-feedback'
const REPO_PULLS_FEEDBACK_SUBJECT = 'repo_pulls_dashboard_feedback'

export function RepoPullsDashboardHeader({showSidebar}: {showSidebar: boolean}) {
  const {fetchedQuery, navigateToResults, owner, repo} = useRepoPullsSearch()
  const {generateLinkPath, selectedLink, sidebarLinks} = useRepoPullsSidebar()
  const {sendAnalyticsEvent} = useAnalytics()
  const [query, setQuery] = useState(fetchedQuery)
  const [previousFetchedQuery, setPreviousFetchedQuery] = useState(fetchedQuery)
  const providers = useFilterProviders({repositoryScope: owner && repo ? `${owner}/${repo}` : undefined})
  const selectedHeaderLink = selectedLink?.id === 'all' ? {...selectedLink, name: 'All pull requests'} : selectedLink

  if (previousFetchedQuery !== fetchedQuery) {
    setPreviousFetchedQuery(fetchedQuery)
    setQuery(fetchedQuery)
  }

  const filterProps: FilterProps = {
    id: 'repo-pulls-dashboard-filter',
    label: 'Search pull requests',
    variant: 'input',
    placeholder: 'Search pull requests',
    filterValue: query,
    providers,
    onChange: setQuery,
    onSubmit: (filterQuery, submitEvent) => {
      navigateToResults({q: filterQuery.raw})
      if (submitEvent === SubmitEvent.ExplicitSubmit) {
        sendAnalyticsEvent('pull-requests.search', 'UPDATE_AND_SUBMIT_QUERY')
        sendCustomMetric({name: 'PULL_REQUESTS_REPO_PULLS_DASHBOARD_SEARCH_SUBMIT', value: 1})
      }
    },
    settings: {groupAndKeywordSupport: true},
  }

  return (
    <SharedHeader
      dashboardType="pull-requests"
      links={sidebarLinks}
      selectedLink={selectedHeaderLink}
      onSelectLink={noop}
      savedViews={EMPTY_SAVED_VIEWS}
      generateLinkPath={generateLinkPath}
      previewMenu={<RepoPullsPreviewMenu />}
      optOutLocked={isFeatureEnabled('repo_pulls_dashboard_ga')}
      showOptionsButton={false}
      showSavedViews={false}
      showSidebarTrigger={showSidebar}
      primaryAction={<RepoPullsDashboardActions />}
      filter={
        <>
          <div role="search" aria-label="Pull requests">
            <Filter {...filterProps} data-testid="repo-pulls-dashboard-filter" />
          </div>
          <div className={styles.MobileFeedback}>
            <RepoPullsDashboardFeedback />
          </div>
        </>
      }
    />
  )
}

function RepoPullsPreviewMenu() {
  const {owner, repo} = useRouteParams(repoPullsDashboardContentRoute)
  const {sendAnalyticsEvent} = useAnalytics()
  const reportError = useReportErrorContext()
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const {isBrowser} = useRuntimeEnvironment()
  const staff = isBrowser && isStaff()
  if (!owner || !repo) return null

  return (
    <>
      <ActionMenu anchorRef={menuTriggerRef}>
        <ActionMenu.Button ref={menuTriggerRef} aria-label="Preview options" variant="invisible" size="small">
          <Label variant="success" className={styles.PreviewLabel}>
            Preview
          </Label>
        </ActionMenu.Button>
        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.LinkItem href="https://gh.io/repo-pulls-learn-more" target="_blank" rel="noopener noreferrer">
              <ActionList.LeadingVisual>
                <RocketIcon />
              </ActionList.LeadingVisual>
              What&apos;s new
              <ActionList.TrailingVisual>
                <ArrowUpRightIcon />
              </ActionList.TrailingVisual>
            </ActionList.LinkItem>
            {staff ? (
              <ActionList.LinkItem href={STAFF_FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
                <ActionList.LeadingVisual>
                  <CommentIcon />
                </ActionList.LeadingVisual>
                Give feedback
                <ActionList.TrailingVisual>
                  <ArrowUpRightIcon />
                </ActionList.TrailingVisual>
              </ActionList.LinkItem>
            ) : (
              <ActionList.Item onSelect={() => feedbackRef.current?.openDialog()}>
                <ActionList.LeadingVisual>
                  <CommentIcon />
                </ActionList.LeadingVisual>
                Give feedback
              </ActionList.Item>
            )}
            <ActionList.Divider />
            <ActionList.Item
              onSelect={async () => {
                try {
                  const response = await reactFetch('/feature_preview/repo_pulls_dashboard_surface/toggle', {
                    method: 'POST',
                  })
                  if (!response.ok) throw new Error('Failed to leave the repository pulls preview')
                  sendAnalyticsEvent('pull-requests.click', 'SWITCH_TO_CLASSIC')
                  navigateToRepoPulls(repoPullRequestsPath(owner, repo))
                } catch (error) {
                  reportError(error)
                }
              }}
            >
              <ActionList.LeadingVisual>
                <ArrowSwitchIcon />
              </ActionList.LeadingVisual>
              Switch back to classic page
            </ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {!staff && (
        <DashboardFeedbackDialog
          ref={feedbackRef}
          subject={REPO_PULLS_FEEDBACK_SUBJECT}
          placeholderText="Tell us your feedback on the new pull request experience"
          hideButton
          returnFocusRef={menuTriggerRef}
        />
      )}
    </>
  )
}

export function RepoPullsDashboardCollapsibleSidebar() {
  const {generateLinkPath, selectedLink, sidebarLinks} = useRepoPullsSidebar()

  return (
    <>
      <DashboardSidebarCollapsible
        dashboardType="pull-requests"
        links={sidebarLinks}
        selectedLink={selectedLink?.id || ''}
        onSelectLink={noop}
        savedViews={EMPTY_SAVED_VIEWS}
        generateLinkPath={generateLinkPath}
      />
      <RepoPullsDashboardPages />
    </>
  )
}

function RepoPullsDashboardPages() {
  const {data: layoutPayload} = useRouteQuery(repoPullsDashboardLayoutRoute, 'mainQuery')
  const {labels, milestones} = layoutPayload?.actions ?? {}

  if (!isFeatureEnabled('repo_pulls_dashboard_sidebar_links') || (!labels && !milestones)) return null

  return (
    <>
      <hr className={styles.SidebarDivider} />
      <CollapsibleNavList aria-label="Pages">
        {milestones ? (
          <CollapsibleNavList.LinkItem to={milestones.url} icon={MilestoneIcon} label="Milestones" />
        ) : null}
        {labels ? <CollapsibleNavList.LinkItem to={labels.url} icon={TagIcon} label="Labels" /> : null}
      </CollapsibleNavList>
    </>
  )
}

export function RepoPullsDashboardFeedback() {
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const triggerRef = useRef<HTMLLIElement>(null)
  const {isBrowser} = useRuntimeEnvironment()
  const {visualIsExpanded} = useCollapsibleSidebarContext()
  const staff = isBrowser && isStaff()
  if (!isFeatureEnabled('repo_pulls_dashboard_ga')) return null

  return (
    <>
      <SidebarTooltip text="Feedback" disabled={visualIsExpanded}>
        <ActionList className={styles.FeedbackLinkWrapper}>
          {staff ? (
            <ActionList.LinkItem href={STAFF_FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
              <ActionList.LeadingVisual>
                <CommentIcon />
              </ActionList.LeadingVisual>
              Feedback
            </ActionList.LinkItem>
          ) : (
            <ActionList.Item ref={triggerRef} onSelect={() => feedbackRef.current?.openDialog()}>
              <ActionList.LeadingVisual>
                <CommentIcon />
              </ActionList.LeadingVisual>
              Feedback
            </ActionList.Item>
          )}
        </ActionList>
      </SidebarTooltip>
      {!staff && (
        <DashboardFeedbackDialog
          ref={feedbackRef}
          subject={REPO_PULLS_FEEDBACK_SUBJECT}
          placeholderText="Tell us your feedback on the new pull request experience"
          hideButton
          returnFocusRef={triggerRef}
        />
      )}
    </>
  )
}

function RepoPullsDashboardActions() {
  const {data: layoutPayload} = useRouteQuery(repoPullsDashboardLayoutRoute, 'mainQuery')
  const actions = layoutPayload?.actions
  const disabledReasonId = useId()
  if (!actions) return null

  const {agentTaskRepository, labels, milestones, newPullRequest} = actions
  const sidebarLinksEnabled = isFeatureEnabled('repo_pulls_dashboard_sidebar_links')
  return (
    <div className="d-flex flex-wrap gap-2">
      {!sidebarLinksEnabled && labels ? (
        <Button as={Link} to={labels.url} leadingVisual={TagIcon}>
          Labels <CounterLabel>{labels.count}</CounterLabel>
        </Button>
      ) : null}
      {!sidebarLinksEnabled && milestones ? (
        <Button as={Link} to={milestones.url} leadingVisual={MilestoneIcon}>
          Milestones <CounterLabel>{milestones.count}</CounterLabel>
        </Button>
      ) : null}
      {agentTaskRepository ? (
        <NewRepoAgentTaskButton
          repository={repoPullsDashboardRepositoryToRepository(agentTaskRepository)}
          icon={AgentIcon}
          label="New agent session"
          eventType="new_agent_task_button_repo_pulls"
        />
      ) : null}
      {newPullRequest?.url ? (
        <Button as={Link} to={newPullRequest.url} variant="primary">
          {newPullRequest.label}
        </Button>
      ) : newPullRequest?.disabledReason ? (
        <>
          <Button
            variant="primary"
            inactive
            aria-disabled="true"
            aria-describedby={disabledReasonId}
            title={newPullRequest.disabledReason}
          >
            {newPullRequest.label}
          </Button>
          <span id={disabledReasonId} className="sr-only">
            {newPullRequest.disabledReason}
          </span>
        </>
      ) : null}
    </div>
  )
}
